import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Flag, Trash2, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

// 13-language mini dictionary for the comments component.
const C_L = {
  fr: { count: 'commentaire', addPh: 'Ajouter un commentaire...', loading: 'Chargement…', beFirst: 'Soyez le premier à commenter' },
  en: { count: 'comment', addPh: 'Add a comment…', loading: 'Loading…', beFirst: 'Be the first to comment' },
  es: { count: 'comentario', addPh: 'Agregar un comentario…', loading: 'Cargando…', beFirst: 'Sé el primero en comentar' },
  pt: { count: 'comentário', addPh: 'Adicionar um comentário…', loading: 'A carregar…', beFirst: 'Seja o primeiro a comentar' },
  de: { count: 'Kommentar', addPh: 'Kommentar hinzufügen…', loading: 'Lädt…', beFirst: 'Sei der Erste, der kommentiert' },
  it: { count: 'commento', addPh: 'Aggiungi un commento…', loading: 'Caricamento…', beFirst: 'Sii il primo a commentare' },
  ru: { count: 'комментарий', addPh: 'Добавить комментарий…', loading: 'Загрузка…', beFirst: 'Оставьте первый комментарий' },
  ar: { count: 'تعليق', addPh: 'أضف تعليقًا…', loading: 'جارٍ التحميل…', beFirst: 'كن أول من يعلّق' },
  zh: { count: '条评论', addPh: '添加评论…', loading: '加载中…', beFirst: '成为第一个评论者' },
  ja: { count: '件のコメント', addPh: 'コメントを追加…', loading: '読み込み中…', beFirst: '最初のコメントを投稿しよう' },
  hi: { count: 'टिप्पणी', addPh: 'टिप्पणी जोड़ें…', loading: 'लोड हो रहा है…', beFirst: 'सबसे पहले टिप्पणी करें' },
  nl: { count: 'reactie', addPh: 'Reactie toevoegen…', loading: 'Laden…', beFirst: 'Wees de eerste die reageert' },
  sw: { count: 'maoni', addPh: 'Ongeza maoni…', loading: 'Inapakia…', beFirst: 'Kuwa wa kwanza kutoa maoni' },
  ht: { count: 'kòmantè', addPh: 'Ajoute yon kòmantè…', loading: 'K ap chaje…', beFirst: 'Se ou premye a ki kòmante' },
};

const CommentSection = ({ postId, commentsCount, onCommentAdded }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const { user } = useAuth();
  const { language } = useLanguage();
  const cL = C_L[language] || C_L.fr;

  useEffect(() => {
    if (showComments) {
      loadComments();
    }
  }, [showComments]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/gim-toli/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await axios.post(`/api/gim-toli/${postId}/comment`, null, {
        params: { content: newComment }
      });
      
      await loadComments();
      setNewComment('');
      toast.success('Commentaire ajouté');
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleAddReply = async (parentCommentId) => {
    if (!replyContent.trim()) return;

    try {
      await axios.post(`/api/gim-toli/${postId}/comment`, null, {
        params: {
          content: replyContent,
          parent_comment_id: parentCommentId
        }
      });
      
      await loadComments();
      setReplyContent('');
      setReplyTo(null);
      toast.success('Réponse ajoutée');
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Erreur lors de l\'ajout de la réponse');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`/api/comments/${commentId}`);
      toast.success('Commentaire supprimé');
      await loadComments();
      if (onCommentAdded) onCommentAdded();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReportComment = async () => {
    if (!reportReason.trim()) {
      toast.error('Veuillez indiquer la raison du signalement');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('comment_id', reportingComment);
      formData.append('post_id', postId);
      formData.append('reason', reportReason);
      
      await axios.post('/api/reports/comment', formData);
      toast.success('Signalement envoyé');
      setReportingComment(null);
      setReportReason('');
    } catch (error) {
      console.error('Error reporting comment:', error);
      toast.error('Erreur lors du signalement');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}j`;
  };

  return (
    <div className="border-t border-white/10 pt-4 mt-4">
      {/* Toggle comments button - Shows count and expands */}
      <button
        onClick={() => setShowComments(!showComments)}
        className="flex items-center justify-between w-full text-gray-400 hover:text-white transition-colors mb-4 group"
      >
        <div className="flex items-center space-x-2">
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">{commentsCount || 0} {cL.count}{(commentsCount || 0) > 1 && (language === 'fr' || language === 'en' || language === 'es' || language === 'pt' || language === 'de' || language === 'it' || language === 'nl') ? 's' : ''}</span>
        </div>
        {showComments ? (
          <ChevronUp className="w-5 h-5 text-gray-500 group-hover:text-white" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-white" />
        )}
      </button>

      {showComments && (
        <div className="space-y-4">
          {/* Add comment input */}
          {user && (
            <div className="flex space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                {user.profile_image_url ? (
                  <img src={getMediaUrl(user.profile_image_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-bold">{user.name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                    placeholder={cL.addPh}
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500/50 text-sm"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="p-2 bg-magenta-500 hover:bg-magenta-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-full transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Comments list */}
          {loading ? (
            <div className="text-center text-gray-400 py-4">{cL.loading}</div>
          ) : comments.length === 0 ? (
            <div className="text-center text-gray-500 py-4">{cL.beFirst}</div>
          ) : (
            <div className="space-y-4">
              {comments.map((comment) => (
                <div key={comment.id} className="space-y-3">
                  <div className="flex space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {comment.user?.profile_image_url ? (
                        <img src={getMediaUrl(comment.user.profile_image_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold">{comment.user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="bg-white/5 rounded-2xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-sm">{comment.user?.name || 'Utilisateur'}</span>
                            <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-200">{comment.content}</p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-4 mt-2 ml-3">
                        {user && (
                          <button
                            onClick={() => setReplyTo(comment.id)}
                            className="text-xs text-gray-400 hover:text-violet-400 transition-colors"
                          >
                            Répondre
                          </button>
                        )}
                        {user && comment.user_id !== user.id && (
                          <button
                            onClick={() => setReportingComment(comment.id)}
                            className="text-xs text-gray-400 hover:text-red-400 transition-colors flex items-center space-x-1"
                          >
                            <Flag className="w-3 h-3" />
                            <span>Signaler</span>
                          </button>
                        )}
                        {user && comment.user_id === user.id && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-xs text-gray-400 hover:text-red-400 transition-colors flex items-center space-x-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            <span>Supprimer</span>
                          </button>
                        )}
                      </div>

                      {/* Reply input */}
                      {replyTo === comment.id && user && (
                        <div className="mt-3 flex space-x-2">
                          <input
                            type="text"
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddReply(comment.id)}
                            placeholder={`Répondre à ${comment.user?.name}...`}
                            className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500/50 text-sm"
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddReply(comment.id)}
                            disabled={!replyContent.trim()}
                            className="p-2 bg-violet-500 hover:bg-violet-600 disabled:bg-gray-700 rounded-full transition-colors"
                          >
                            <Send className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => {
                              setReplyTo(null);
                              setReplyContent('');
                            }}
                            className="p-2 bg-gray-700 hover:bg-gray-600 rounded-full transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Replies */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="ml-11 space-y-3">
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="flex space-x-3">
                          <div className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {reply.user?.profile_image_url ? (
                              <img src={getMediaUrl(reply.user.profile_image_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[10px] font-bold">{reply.user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="bg-white/5 rounded-xl p-2">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-semibold text-xs">{reply.user?.name || 'Utilisateur'}</span>
                                <span className="text-[10px] text-gray-500">{formatDate(reply.created_at)}</span>
                              </div>
                              <p className="text-xs text-gray-200">{reply.content}</p>
                            </div>
                            <div className="flex items-center space-x-3 mt-1 ml-2">
                              {user && reply.user_id !== user.id && (
                                <button
                                  onClick={() => setReportingComment(reply.id)}
                                  className="text-[10px] text-gray-400 hover:text-red-400 transition-colors"
                                >
                                  Signaler
                                </button>
                              )}
                              {user && reply.user_id === user.id && (
                                <button
                                  onClick={() => handleDeleteComment(reply.id)}
                                  className="text-[10px] text-gray-400 hover:text-red-400 transition-colors"
                                >
                                  Supprimer
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report Modal */}
      {reportingComment && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Signaler ce commentaire</h3>
              <button
                onClick={() => {
                  setReportingComment(null);
                  setReportReason('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Pourquoi signalez-vous ce commentaire ?"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 resize-none"
              rows={3}
            />
            
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => {
                  setReportingComment(null);
                  setReportReason('');
                }}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleReportComment}
                disabled={!reportReason.trim()}
                className="flex-1 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-700 rounded-xl transition-colors"
              >
                Signaler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default CommentSection;
